import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCog, faDownload, faPencil, faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, IconWithMenu, Link, ListItem, StatusBadge, Text,
} from '@domain-group/suit-theme'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

import type { Item, UseCampaignAccountingPayload } from '@hooks/useCampaignAccounting'

import { confirmDelete } from '@functions/entity'

const itemColors = {
  bill: {
    color: 'blueGrey',
  },
  credit: {
    color: 'green',
  },
  invoice: {
    color: 'orange',
  },
  payment: {
    color: 'purple',
  },
}

interface StatementHistoryListItemProps {
  callbacks: UseCampaignAccountingPayload['callbacks'],
  item: Item,
}

const StatementHistoryListItem = (props: StatementHistoryListItemProps) => {
  const {
    callbacks: {
      editItem,
      deleteItem,
    },
    item,
    item: {
      amount,
      amountType,
      authenticatedDownloadUrl,
      date,
      deletable,
      downloadUrl,
      editable,
      itemType,
      status,
      tags,
      title,
      tooltip,
    },
  } = props

  // @ts-expect-error TS7053
  const { color } = itemColors[itemType] || {}

  // editable items are credit notes only
  const showCog = authenticatedDownloadUrl || editable || deletable || downloadUrl

  const confirmDeletePayload = {
    callbacks: {
      deleteFn: () => deleteItem(item),
    },
    strings: {
      confirmString: 'Are you sure? This will delete the Accounting Item.',
      entityName: 'Accounting Item',
    },
  }

  return (
    <ListItem
      boxProps={{
        alignItems: 'center',
        borderBottom: '1px dashed',
        padding: 'large',
      }}
      disableHover
    >
      <Box flexDirection="column">
        <Text fontSize="small">{title}</Text>

        <Text display="flex" fontSize="xsmall" marginTop="small">
          {tooltip && (
            <Text color="bodyFontLightColor" display={['none', 'flex']} marginRight="small">
              {tooltip} |
            </Text>
          )}

          {tags.map(tag => (
            <Text color="bodyFontLightColor" key={tag} marginRight="small">
              {tag}
            </Text>
          ))}
        </Text>

        <StatusBadge
          boxProps={{ marginTop: 'small' }}
          color={color}
          text={itemType}
        />
      </Box>

      <Box alignItems="flex-end" flexDirection="column" marginLeft="auto" width="auto" marginRight="large">
        <Text color={amountType === 'credit' ? 'green' : 'bodyFontColor'} fontSize="small">
          {amountType === 'debit' && '-'}
          {formatMoney(amount)}
        </Text>

        <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="small" style={{ whiteSpace: 'pre' }}>
          {status}
        </Text>

        <Text color="lightGrey" fontSize="xsmall" marginTop="small" style={{ whiteSpace: 'pre' }}>
          {date}
        </Text>
      </Box>

      <Box width={16}>
        {showCog && (
          <IconWithMenu
            icon={(
              <Link greyLink>
                <FontAwesomeIcon icon={faCog} />
              </Link>
            )}
          >
            {/*
             // @ts-expect-error TS2741 */}
            <IconWithMenu.Menu closeOnClick>
              {editable && (
                <Link key="edit-item" onClick={() => editItem(item)}>
                  <FontAwesomeIcon icon={faPencil} /> Edit Item
                </Link>
              )}

              {authenticatedDownloadUrl && (
                <AuthenticatedDownload key="download-link">
                  <Link href={authenticatedDownloadUrl} target="_blank">
                    <FontAwesomeIcon icon={faDownload} /> Download Item
                  </Link>
                </AuthenticatedDownload>
              )}

              {downloadUrl && (
                <Link key="download-item" href={downloadUrl} target="_blank">
                  <FontAwesomeIcon icon={faDownload} /> Download Item
                </Link>
              )}

              {deletable && (
                // @ts-expect-error TS2345
                <Link onClick={() => confirmDelete(confirmDeletePayload)}>
                  <FontAwesomeIcon icon={faTrashAlt} /> Delete Item
                </Link>
              )}
            </IconWithMenu.Menu>
          </IconWithMenu>
        )}
      </Box>
    </ListItem>
  )
}

export default StatementHistoryListItem
